import { ReactNode } from 'react';
import { VisibilityState } from '@tanstack/react-table';
import { influencer } from '@round/api';
import { FlagIcon } from '@round/creatorbase-ui';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { platformLabels } from 'Modules/Plans/helpers';
import PlatformSelect from '../../../CreatorDetailsModal/PlatformSelect/PlatformSelect';
import {
    DATE_ADDED_COLUMN_ID,
    EXPLORE_PLATFORMS,
    ExplorePlatform,
    getDateAddedValue,
    getDraftTimeValue,
    getLastCollabValue,
    getMatchScoreValue,
    MATCH_SCORE_COLUMN_ID,
} from '../../helpers';
import { getCreatorLocation, getTopAudienceCountries } from '../../cells/LocationCell/LocationCell';
import { ResponseRate } from '../../cells/ResponseRateCell/ResponseRateCell';
import {
    getAvgCpeValue,
    getCostValue,
    getCpmValue,
    getEngagementRateValue,
    getFollowersValue,
    getPlaysValue,
    getPostingFrequencyValue,
} from '../../cells/PlatformStatCells/PlatformStatCells';
import styles from './CreatorDetailsPanel.module.css';

const HEADING = 'Creator details';
const COLLAPSE_HINT = 'Click the row again to collapse';
const NO_VALUE = '-';

type Props = {
    creator: influencer.CreatorSearchResult;
    /** The platform the row's stats are read off, the same one its Platform column is on. */
    platform: ExplorePlatform;
    /** Set only for a creator with an account on more than one platform, since there's nothing to switch otherwise. */
    switchablePlatforms?: ExplorePlatform[];
    onPlatformSelect: (platform: ExplorePlatform) => void;
    /** Which of the preset's columns are on, so the panel lists the same stats the table does. */
    columnVisibility: VisibilityState;
};

/** The right-hand side of an expanded creator row: the row's stats, and a switch between their platforms. */
export const CreatorDetailsPanel = ({
    creator,
    platform,
    switchablePlatforms,
    onPlatformSelect,
    columnVisibility,
}: Props) => {
    const { getCurrencyById } = useCurrencies();

    const statsItems = getStatsItems(creator, platform, columnVisibility, getCurrencyById).filter(
        // The switch already says which platform the stats are for.
        (item) => !(switchablePlatforms && item.columnId === 'platform')
    );

    return (
        <section className={styles.container} aria-label={HEADING}>
            <p className={styles.heading}>{HEADING}</p>

            {switchablePlatforms && (
                <PlatformSelect
                    availablePlatforms={switchablePlatforms}
                    value={platform}
                    onValueChange={(value) => {
                        const nextPlatform = EXPLORE_PLATFORMS.find((explorePlatform) => explorePlatform === value);
                        if (nextPlatform) {
                            onPlatformSelect(nextPlatform);
                        }
                    }}
                />
            )}

            <div className={styles.stats}>
                {statsItems.map((item) => (
                    <div key={item.label} className={styles.statRow}>
                        <span className={styles.statLabel}>{item.label}</span>
                        <span className={styles.statValue}>{item.value}</span>
                    </div>
                ))}
            </div>

            <p className={styles.collapseHint}>{COLLAPSE_HINT}</p>
        </section>
    );
};

/** In the table's column order, each under the id of the column it mirrors, so it follows that column's visibility. */
const getStatsItems = (
    creator: influencer.CreatorSearchResult,
    platform: ExplorePlatform,
    columnVisibility: VisibilityState,
    getCurrencyById: ReturnType<typeof useCurrencies>['getCurrencyById']
) => {
    const { country, label: locationLabel } = getCreatorLocation(creator);
    const audienceCountries = getTopAudienceCountries(creator);
    const tags = creator.influencer_tags ?? [];

    const items: { columnId: string; label: string; value: ReactNode }[] = [
        { columnId: MATCH_SCORE_COLUMN_ID, label: 'Match', value: getMatchScoreValue(creator) },
        {
            columnId: 'location',
            label: 'Location',
            value: (
                <span className={styles.inline}>
                    {country && <FlagIcon countryCode={country.code} />}
                    {locationLabel}
                </span>
            ),
        },
        {
            columnId: 'location',
            label: 'Top Audience',
            value: audienceCountries.length ? (
                <span className={styles.inline}>
                    {audienceCountries.map((audienceCountry) => (
                        <FlagIcon key={audienceCountry.code} countryCode={audienceCountry.code} />
                    ))}
                </span>
            ) : (
                NO_VALUE
            ),
        },
        { columnId: 'tags', label: 'Tags', value: tags.length ? tags.join(', ') : NO_VALUE },
        { columnId: 'metrics.avg_draft_time_in_hours', label: 'Draft Time', value: getDraftTimeValue(creator) },
        {
            columnId: 'metrics.response_rate',
            label: 'Response',
            value: <ResponseRate rate={creator.metrics?.response_rate} />,
        },
        { columnId: 'post_metrics.most_recent_post_date', label: 'Last Collab', value: getLastCollabValue(creator) },
        { columnId: 'platform', label: 'Platform', value: platformLabels[platform] },
        { columnId: 'estimated_cpe', label: 'Avg. CPE', value: getAvgCpeValue(creator, platform, getCurrencyById) },
        {
            columnId: 'total_follower_count',
            label: 'Followers',
            value: getFollowersValue(creator, platform, getCurrencyById),
        },
        { columnId: 'cost', label: 'Cost', value: getCostValue(creator, platform, getCurrencyById) },
        {
            columnId: 'avg_video_views_per_post',
            label: 'Plays',
            value: getPlaysValue(creator, platform, getCurrencyById),
        },
        { columnId: 'estimated_cpm', label: 'CPM', value: getCpmValue(creator, platform, getCurrencyById) },
        {
            columnId: 'avg_engagement_rate',
            label: 'ER%',
            value: getEngagementRateValue(creator, platform, getCurrencyById),
        },
        {
            columnId: 'post_metrics.activity_rate',
            label: 'Posting Freq',
            value: getPostingFrequencyValue(creator, platform, getCurrencyById),
        },
        { columnId: DATE_ADDED_COLUMN_ID, label: 'Date Added', value: getDateAddedValue(creator) },
    ];

    return items.filter((item) => columnVisibility[item.columnId] !== false);
};
